import { isBoolean, isNumber, isString } from 'lodash';

import * as K from 'common/constants';
import { Settings } from 'common/types';
import { LogEntry, LogEntryEdge, SystemField } from 'common/graphqlTypes';
import { PlaceholderLogEntryEdge } from 'hooks/useLRQ/useEventCache';
import { isRecord } from 'common/util/object';
import { convertTimestampToMilliseconds, timezoneMoment } from 'common/util/time';

type DisplayReadyValue = string | number | boolean;
export type CustomLogValue = {
  type:  string;
  value: DisplayReadyValue;
};

export type SystemLogValue = {
  type:  SystemField;
  value: string;
};

type LogEntryMaps = {
  serverInfo?: Record<string, unknown>;
  values?:     Record<string, unknown>;
};

type TimezonePreference = {
  timezone: string | null
};

// I decided to stiffen the types here to involve TypeScript fully
// in the development. Both types are very permissive and do not require checks
// because of using `any` as their core parts. This characteristic leads
// to relaxed and fast programming but consequent runtime errors in
// a production environment.
export type StrictLogEntry = Omit<LogEntry, keyof LogEntryMaps> & LogEntryMaps;
export type StrictSettings = typeof K.DEFAULT_SETTINGS & TimezonePreference;

type CustomLogEntrySelect = (logEntry: StrictLogEntry, settings: StrictSettings) => CustomLogValue | null;
type SystemLogEntrySelect = (logEntry: StrictLogEntry, settings?: StrictSettings) => SystemLogValue | null;
type LogEntrySelect = CustomLogEntrySelect | SystemLogEntrySelect;

export enum FIELD_CATEGORIES {
  SYSTEM,
  CUSTOM,
}

type CustomLogEntrySelector = {
  type:     string;
  category: FIELD_CATEGORIES;
  select:   CustomLogEntrySelect;
};

type SystemLogEntrySelector = {
  type:     SystemField;
  category: FIELD_CATEGORIES;
  select:   SystemLogEntrySelect;
};

type LogEntrySelector = SystemLogEntrySelector | CustomLogEntrySelector;

const prepareDateTimeValue = (
  logEntry: StrictLogEntry,
  format:   string,
  type:     SystemField.date | SystemField.timestamp,
  timezone: string | null,
): SystemLogValue | null => {

  if (!logEntry.timestamp) {
    return null;
  }
  const timestamp = convertTimestampToMilliseconds(logEntry.timestamp);
  const value = timezoneMoment(timezone)(timestamp).format(format);

  return { type, value };
};

export const prepareDate: SystemLogEntrySelect = (logEntry, settings) =>
  prepareDateTimeValue(logEntry, 'MMM D', SystemField.date, settings?.timezone ?? null);

export const prepareTime: SystemLogEntrySelect = (logEntry, settings) =>
  prepareDateTimeValue(logEntry, 'HH:mm:ss.SSS', SystemField.timestamp, settings?.timezone ?? null);

/**
 * Verifies if something can communicate meaning by its value.
 *
 * @param something the unknown to verify.
 */
export const isMeaningfulValue = (something: unknown): boolean => something != null && something !== '';

const getValue = (logEntry: StrictLogEntry, valueName: string): unknown => {

  if (valueName === 'severity') {
    return logEntry.severity;
  }

  if (valueName === 'threadId') {
    return logEntry.threadId;
  }

  const { values } = logEntry;
  if (isRecord(values) && isMeaningfulValue(values[valueName])) {
    return values[valueName];
  }

  const { serverInfo } = logEntry;
  if (isRecord(serverInfo) && isMeaningfulValue(serverInfo[valueName])) {
    return serverInfo[valueName];
  }

  return null;
};

export const isKubernetesCluster = (logEntry: StrictLogEntry) => getValue(logEntry, 'scalyr-category') === 'log';

const getKubernetesController = (
  serverInfo: StrictLogEntry['serverInfo'],
): string | null => {

  if (!isRecord(serverInfo)) {
    return null;
  }

  return (
    Object.keys(serverInfo)
      .find((key) => K.K8S_CONTROLLERS.includes(key as unknown as K.FACET_TERM))
    || null
  );
};

export const prepareHost: SystemLogEntrySelect = (
  logEntry,
) => {

  const serverInfo = logEntry?.serverInfo;
  if (!isRecord(serverInfo)) {
    return null;
  }

  // Handle Kubernetes log entries, which have cluster instead of host.
  if (isKubernetesCluster(logEntry)) {
    const kubernetesClusterName = serverInfo[K.FACET_TERM.KUBERNETES_CLUSTER];

    if (!isString(kubernetesClusterName)) {
      return null;
    }

    return { type: SystemField.host, value: kubernetesClusterName };
  }

  const { serverHost } = serverInfo;
  if (!isString(serverHost)) {
    return null;
  }

  return { type: SystemField.host, value: serverHost };
};

export const prepareLogfileName: SystemLogEntrySelect = (
  logEntry,
) => {

  const serverInfo = logEntry?.serverInfo;
  if (!(isRecord(serverInfo))) {
    return null;
  }

  // Handle Kubernetes log entries, which have controller instead of logfile.
  if (isKubernetesCluster(logEntry)) {
    const controller = getKubernetesController(serverInfo);
    const controllerInfo = controller && serverInfo[controller];

    if (!isString(controllerInfo)) {
      return null;
    }

    return { type: SystemField.logfile, value: controllerInfo };
  }

  const { logfile } = serverInfo;
  if (!isString(logfile)) {
    return null;
  }

  return { type: SystemField.logfile, value: logfile };
};

const isDisplayReadyValue = (value: unknown): value is DisplayReadyValue => (
  isMeaningfulValue(value)
  && (isString(value) || isNumber(value) || isBoolean(value))
);

export const prepareCustomFieldSelect = (field: string): LogEntrySelect => (logEntry: StrictLogEntry): CustomLogValue | null => {
  const value = getValue(logEntry, field);

  if (!isDisplayReadyValue(value)) {
    return null;
  }

  return { type: field, value };
};

export const convertCustomFieldToSelector = (field: string): CustomLogEntrySelector => ({
  type:     field,
  category: FIELD_CATEGORIES.CUSTOM,
  select:   prepareCustomFieldSelect(field),
});

const prepareCustomFieldsSelectors = (logEntry: StrictLogEntry, settings: StrictSettings): CustomLogEntrySelector[] => {
  const additionalUserChosenFields = settings[K.SETTING.ADDITIONAL_FIELDS];

  if (additionalUserChosenFields?.length) {
    return additionalUserChosenFields.map(convertCustomFieldToSelector);
  }

  // Otherwise, we display all server-provided fields and place a tag in the first place.
  // But only when there is no message.
  if (logEntry.values && !isDisplayReadyValue(logEntry.values.message)) {
    const { values } = logEntry;

    return [
      K.FIELD.TAG,
      ...Object.keys(values)
        .filter((field) => field !== K.FIELD.TAG),
    ].map(convertCustomFieldToSelector);
  }

  return [];
};

export const prepareMessage: SystemLogEntrySelect = (logEntry: StrictLogEntry, settings?: StrictSettings) => {
  const message = String(logEntry?.values?.message ?? '');

  if (!(message && isString(message))) {
    return null;
  }

  // Replace escaped returns with real returns based on system setting from /scalyr/logs.
  const displayMessage = settings?.[K.SETTING.UNESCAPE_LOG_LINE_MESSAGE]
    ? message.replace(/\\r/g, '\r').replace(/\\n/g, '\n')
    : message;

  return { type: SystemField.rawLogLine, value: displayMessage.trim() };
};

const systemDataSelectors: SystemLogEntrySelector[] = [
  { type: SystemField.date,      category: FIELD_CATEGORIES.SYSTEM, select: prepareDate },
  { type: SystemField.timestamp, category: FIELD_CATEGORIES.SYSTEM, select: prepareTime },
  { type: SystemField.host,      category: FIELD_CATEGORIES.SYSTEM, select: prepareHost },
  { type: SystemField.logfile,   category: FIELD_CATEGORIES.SYSTEM, select: prepareLogfileName },
];

// Message selector exists in the form of the array for more convenient further destructuring in
// the prepareDisplayedFieldsSelectors function.
const messageSelectors: SystemLogEntrySelector[] = [
  { type: SystemField.rawLogLine, category: FIELD_CATEGORIES.SYSTEM, select: prepareMessage },
];

const keepChosenSystemSettings = (
  settings: StrictSettings,
) => ({ type }: SystemLogEntrySelector): boolean => settings[K.SETTING.SYSTEM_FIELDS].includes(type);

/**
 * Prepare an array of selectors to extract the required log line data and work with it in the flattened format.
 * Useful for further styling on the pages with log lines as well as preparing data for download.
 *
 * CONSIDER: Eliminating the dependency upon logEntry somehow.
 *  By doing so we can prepare a single array of selectors and not to recalculate it every time.
 *
 * @param logEntry  information about particular log entry.
 * @param settings  a set of user preferences.
 */
export const prepareDisplayedFieldsSelectors = (logEntry: StrictLogEntry, settings: StrictSettings): LogEntrySelector[] => [
  ...systemDataSelectors.filter(keepChosenSystemSettings(settings)),
  ...prepareCustomFieldsSelectors(logEntry, settings),
  ...messageSelectors.filter(keepChosenSystemSettings(settings)),
];

/**
 * Get the displayed form of the message field. A long message may be truncated.
 *
 * @param logEntry    log line
 * @param settings    user settings
 *
 * @returns the possibly truncated message
 */
const getDisplayMessage = (logEntry: StrictLogEntry, settings: Settings): string => {

  const serverFields = settings[K.SETTING.SYSTEM_FIELDS];
  const message = String(logEntry?.values?.message ?? '');
  let displayMessage = settings[K.SETTING.UNESCAPE_LOG_LINE_MESSAGE] ? message.replace(/\\r/g, '\r').replace(/\\n/g, '\n') : message;
  displayMessage = displayMessage.trim();
  if (!displayMessage || !serverFields.includes(SystemField.rawLogLine)) {
    return '';
  }

  // check whether message will be truncated based on number of lines
  const lines = displayMessage.split(K.REGEX.LINE_RETURN);
  if (lines.length > K.MAX_FACET_VALUE_LINES) {
    displayMessage = lines.slice(0, K.MAX_FACET_VALUE_LINES).join('\n');
  }

  return displayMessage;
};

/**
 * Return a function to calculate the height needed to display a log line, based on the number of line returns.
 * Message is the only field that respects line returns.
 *
 * @param logLines    list of log lines
 * @param settings    user settings
 */
export const getRowSizeCalculator = (
  logLines: Array<LogEntryEdge | PlaceholderLogEntryEdge>,
  settings: Settings,
) => (index: number): number => {

  const { logEntry } = logLines[index];
  const displayMessage = logEntry ? getDisplayMessage(logEntry, settings) : '';
  if (!displayMessage) {
    return K.LOGLINE_HEIGHT + K.LOGLINE_EXTRA_SPACE;
  }
  const numVisualLines = displayMessage.split(K.REGEX.LINE_RETURN).length;
  return (numVisualLines * K.LOGLINE_HEIGHT) + K.LOGLINE_EXTRA_SPACE;
};
